import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { DocumentModel } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { Collapsable } from "@ucs/Components/Collapsable";
import ChevronRight from "@ucs/Icons/ChevronRight";
import UCSColors from "@ucs/Utils/UCSColors";
import parse from "html-react-parser";
import isArray from "lodash/isArray";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";


type Props =
    ReturnType<typeof mapStateToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    state
});

const enum fields {
    title = "title",
    links = "links",
    direction = "direction",
    alignment = "alignment",
    opacity = "opacity",
    backgroundColor = "backgroundColor",
    hoverColor = "hoverColor",
}

const enum icons {
    instagram = "Instagram",
    facebook = "Facebook",
    youtube = "Youtube",
    twitter = "Twitter",
    linkedin = "LinkedIn",
    newsletter = "Newsletter",
}

// const testString = "<p><a href=\"* https://uniboard.com/images/centre-de-documentation/FR/Panneaux/Brochures/Flipchart/Flipchart_Synchro_2021-2022.pdf\" rel=\"noopener noreferrer\" target=\"_blank\">Uniboard is cool</a></p><p><a href=\"https://projectpoplar.commerce.insitesandbox.com/ContactUs\" rel=\"noopener noreferrer\" target=\"_blank\">Contact Us</a></p><p><a href=\"https://projectpoplar.commerce.insitesandbox.com/Product/H54-skye-woodgrain-uniboard\" rel=\"noopener noreferrer\" target=\"_blank\">And this is a 3rd link.</a></p>";

    
const ProductLinksContainer = styled.div`
    background-color: white;
    padding: 22px 10% 10%;
    height: 100%;
    ul {
        font-size: 18px;
        li {
            margin-bottom: 25px;
            > span {
                margin-right: 8px;
            }
        }
    }
    ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                padding: 28px 0;
                ul {
                    font-size: 17px;
                    li {
                        margin-bottom: 18px;
                    }
                }
            `,
            css`
                padding: 28px 0;
            `,
        ]);
    }}
`;

const MobileContainer = styled.div`
    display: none;
    ${({ theme }) => 
            (breakpointMediaQueries(theme, 
            [css`display: block;`,
            css`display: block;`,]))};
`;

const Container = styled.div`
    height: 100%;
    ${({ theme }) => 
        (breakpointMediaQueries(theme, 
        [css`display: none;`,
        css`display: none;`,
        ]))};
`;

const parseLinks : any = (
    node: string | React.DetailedReactHTMLElement<{}, HTMLElement> | React.DetailedReactHTMLElement<{}, HTMLElement>[],
    parsedLinks: any[]
) => {
    if (isArray(node)) {
        node.forEach((childNode) => {
            parseLinks(childNode, parsedLinks);
        })
    } else if (typeof node === "object") {
            if ("type" in node && node.type === "a") {
                if ((node.props as any)?.children && (node.props as any)?.href) {
                    parsedLinks.push({href:(node.props as any)?.href, text: (node.props as any)?.children});
                }
            } else if (
                "props" in node &&
                "children" in node.props &&
                node.props.children != null
            ) {
                if (isArray(node.props.children)) {
                    if (node.props.children.forEach) {
                        node.props.children.forEach(
                            (
                                child:
                                    | React.DetailedReactHTMLElement<{ children: any }, HTMLElement>
                                    | React.DetailedReactHTMLElement<{}, HTMLElement>[],
                            ) => {
                                parseLinks(child, parsedLinks);
                            },
                        );
                    }
                } else {
                    parseLinks(node.props.children, parsedLinks);
                }
            }   
        }
}

const ProductLinks: React.FC<Props> = ({
    productContext: { product, productInfo },
    state
}: Props) => {
    const links : DocumentModel[] = [];
    if (product.documents) {
        product.documents.forEach((document) => {
            if (document.name !== `${product.productNumber}.zip`) {
                links.push(document);
            }
        });
    }

    links.sort((a, b) => {
        return a.name.localeCompare(b.name);
    });

    const parsedLinks : any[] = [];

    if (product.specifications) {
        product.specifications.forEach((specification) => {
            if (specification.name.toLowerCase() === "links") {
                parseLinks(parse(specification.htmlContent), parsedLinks);
            }
        });
    }
    
    parsedLinks.sort((a,b) => {
        return a.text.localeCompare(b.text);
    });


    return (
        <>
            <MobileContainer>
                <Collapsable title="Links">
                    <ProductLinksContainer>
                        <ul>
                            {
                                links.length > 0 && links.map((document, idx) => (
                                    <li key={idx}>
                                        <Icon src={ChevronRight} color="primary" size={16}></Icon>
                                        <a href={document.filePath} target="_blank">
                                            {document.name}
                                        </a>
                                    </li>
                                ))
                            }
                            {
                                parsedLinks.length > 0 && parsedLinks.map((link, idx) => (
                                    <li key={idx}>
                                        <Icon src={ChevronRight} color="primary" size={16}></Icon>
                                        <a href={link.href} target="_blank">
                                            {link.text}
                                        </a>
                                    </li>
                                ))
                            }
                        </ul>
                </ProductLinksContainer>
            </Collapsable>
            </MobileContainer>
            <Container>
                <ProductLinksContainer>
                {(links.length > 0 || parsedLinks.length > 0) && (
                    <>
                    <Typography as="h2" variant="h2">
                    Links
                    </Typography>
                    <ul>
                        {
                            links.length > 0 && links.map((document, idx) => (
                                <li key={idx}>
                                    <Icon src={ChevronRight} color="primary" size={16}></Icon>
                                    <a href={document.filePath} target="_blank">
                                        {document.name}
                                    </a>
                                </li>
                            ))
                        }
                        {
                            parsedLinks.length > 0 && parsedLinks.map((link, idx) => (
                                <li key={idx}>
                                    <Icon src={ChevronRight} color="primary" size={16}></Icon>
                                    <a href={link.href} target="_blank">
                                        {link.text}
                                    </a>
                                </li>
                            ))
                        }
                    </ul>
                    </>
                )}
            </ProductLinksContainer>
            </Container>
        </>
    );
};


const contentTab = { displayName: "Content", sortOrder: 1 };


const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const enum alignment {
    left = "left",
    center = "center",
    right = "right",
}

const definition: WidgetDefinition = {
    group: "Product Details",
    icon: "Link",
    isSystem: true,
    allowedContexts: ["ProductDetailsPage"],
    fieldDefinitions: [
        {
            name: fields.links,
            editorTemplate: "ListField",
            tab: contentTab,
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "icon",
                    displayName: "Select Icon",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Instagram", value: icons.instagram },
                        { displayName: "Youtube", value: icons.youtube },
                        { displayName: "Facebook", value: icons.facebook },
                        { displayName: "Twitter", value: icons.twitter },
                        { displayName: "LinkedIn", value: icons.linkedin },
                        { displayName: "Newsletter", value: icons.newsletter },
                    ],
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                    isRequired: true,
                },
                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
            ],
        },
        {
            name: fields.direction,
            editorTemplate: "DropDownField",
            defaultValue: "vertical",
            fieldType: "General",
            options: [
                {
                    displayName: "Vertical",
                    value: "vertical",
                },
                {
                    displayName: "Horizontal",
                    value: "horizontal",
                },
            ],
            hideEmptyOption: true,
            sortOrder: 0,
            tab: settingsTab,
        },
        {
            name: fields.alignment,
            editorTemplate: "DropDownField",
            defaultValue: alignment.center,
            fieldType: "General",
            options: [
                {
                    displayName: "Left",
                    value: alignment.left,
                },
                {
                    displayName: "Center",
                    value: alignment.center,
                },
                {
                    displayName: "Right",
                    value: alignment.right,
                },
            ],
            hideEmptyOption: true,
            tab: settingsTab,
            sortOrder: 1,
        },
        {
            name: fields.opacity,
            displayName: "Opacity",
            editorTemplate: "IntegerField",
            defaultValue: 100,
            min: 1,
            max: 100,
            fieldType: "General",
            sortOrder: 2,
            tab: settingsTab,
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: `${UCSColors.gray03}`,
            fieldType: "General",
            sortOrder: 3,
            tab: settingsTab,
        },
        {
            name: fields.hoverColor,
            displayName: "Hover Color",
            editorTemplate: "ColorPickerField",
            defaultValue: `${UCSColors.gray04}`,
            fieldType: "General",
            sortOrder: 4,
            tab: settingsTab,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(ProductLinks)),
    definition
};

export default widgetModule;
